import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { UserCard } from '../../components/admin/UserCard';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import {
  useGetUsersQuery,
  useToggleUserBanMutation,
  useUpdateUserRoleMutation,
  useDeleteUserMutation,
  useGetUserStatisticsQuery,
} from '../../store/api/adminApi';
import { useLanguage } from '../../hooks/useLanguage';
import { formatDate } from '../../utils/dateFormatter';

export function AdminUsersPage() {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [page, setPage] = useState(1);
  const limit = 20;
  const [isBannedFilter, setIsBannedFilter] = useState<boolean | undefined>(undefined);
  const [isAdminFilter, setIsAdminFilter] = useState<boolean | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'balance' | 'createdAt'>('createdAt');
  const [sortOrder, setSortOrder] = useState<1 | -1>(-1);

  const { data, isLoading, error, refetch } = useGetUsersQuery({
    page,
    limit,
    isBanned: isBannedFilter,
    isAdmin: isAdminFilter,
    search: searchTerm || undefined,
    sortBy,
    sortOrder,
  });

  const { data: statisticsData } = useGetUserStatisticsQuery();

  const [toggleBan, { isLoading: isTogglingBan }] = useToggleUserBanMutation();
  const [updateRole, { isLoading: isUpdatingRole }] = useUpdateUserRoleMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();

  // Dialog states
  const [banDialog, setBanDialog] = useState<{
    isOpen: boolean;
    userId: string | null;
    userName: string;
    isBanned: boolean;
  }>({ isOpen: false, userId: null, userName: '', isBanned: false });

  const [roleDialog, setRoleDialog] = useState<{
    isOpen: boolean;
    userId: string | null;
    userName: string;
    isAdmin: boolean;
  }>({ isOpen: false, userId: null, userName: '', isAdmin: false });

  const [supportDialog, setSupportDialog] = useState<{
    isOpen: boolean;
    userId: string | null;
    userName: string;
    isSupport: boolean;
  }>({ isOpen: false, userId: null, userName: '', isSupport: false });

  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    userId: string | null;
    userName: string;
  }>({ isOpen: false, userId: null, userName: '' });

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBanClick = (userId: string, userName: string, isBanned: boolean) => {
    setBanDialog({ isOpen: true, userId, userName, isBanned });
  };

  const handleBanConfirm = async () => {
    if (!banDialog.userId) return;

    try {
      await toggleBan({
        userId: banDialog.userId,
        isBanned: !banDialog.isBanned,
      }).unwrap();
      toast.success(
        banDialog.isBanned
          ? t('admin.users.unbanSuccess')
          : t('admin.users.banSuccess')
      );
      setBanDialog({ isOpen: false, userId: null, userName: '', isBanned: false });
      void refetch();
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.users.banError')
      );
    }
  };

  const handleRoleClick = (userId: string, userName: string, isAdmin: boolean) => {
    setRoleDialog({ isOpen: true, userId, userName, isAdmin });
  };

  const handleRoleConfirm = async () => {
    if (!roleDialog.userId) return;

    try {
      await updateRole({
        userId: roleDialog.userId,
        isAdmin: !roleDialog.isAdmin,
      }).unwrap();
      toast.success(t('admin.users.roleSuccess'));
      setRoleDialog({ isOpen: false, userId: null, userName: '', isAdmin: false });
      void refetch();
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.users.roleError')
      );
    }
  };

  const handleSupportClick = (userId: string, userName: string, isSupport: boolean) => {
    setSupportDialog({ isOpen: true, userId, userName, isSupport });
  };

  const handleSupportConfirm = async () => {
    if (!supportDialog.userId) return;

    try {
      await updateRole({
        userId: supportDialog.userId,
        role: supportDialog.isSupport ? 'user' : 'admin_support',
      }).unwrap();
      toast.success(t('admin.users.roleSuccess'));
      setSupportDialog({ isOpen: false, userId: null, userName: '', isSupport: false });
      void refetch();
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.users.roleError')
      );
    }
  };

  const handleDeleteClick = (userId: string, userName: string) => {
    setDeleteDialog({ isOpen: true, userId, userName });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.userId) return;

    try {
      await deleteUser({ userId: deleteDialog.userId }).unwrap();
      toast.success(t('admin.users.deleteSuccess'));
      setDeleteDialog({ isOpen: false, userId: null, userName: '' });
      void refetch();
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.users.deleteError')
      );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="text-center py-16">
          <p className="text-neutral-medium text-xl font-body animate-pulse">
            {t('common.loading')}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen">
        <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
          <h2 className="text-2xl font-heading font-bold text-danger mb-4">
            {t('admin.users.error.title')}
          </h2>
          <p className="text-neutral-medium font-body mb-6">
            {'data' in (error as { data?: { message?: string } })
              ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
              : t('errors.network')}
          </p>
          <button
            onClick={() => void refetch()}
            className="px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  const users = data?.data.users || [];
  const pagination = data?.data.pagination;
  const statistics = statisticsData?.data.statistics;

  return (
    <div className="min-h-screen animate-slide-up">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-2">
          {t('admin.users.title')}
        </h1>
        <p className="text-neutral-medium text-lg font-body">
          {t('admin.users.subtitle')}
        </p>
      </div>

      {/* Statistics */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="card-glass rounded-lg p-4 border border-neutral-dark">
            <div className="text-sm text-neutral-medium font-body mb-1">
              {t('admin.users.stats.total')}
            </div>
            <div className="text-2xl font-heading font-bold text-brand-accent">
              {statistics.totalUsers}
            </div>
          </div>
          <div className="card-glass rounded-lg p-4 border border-neutral-dark">
            <div className="text-sm text-neutral-medium font-body mb-1">
              {t('admin.users.stats.active')}
            </div>
            <div className="text-2xl font-heading font-bold text-green-500">
              {statistics.activeUsers}
            </div>
          </div>
          <div className="card-glass rounded-lg p-4 border border-neutral-dark">
            <div className="text-sm text-neutral-medium font-body mb-1">
              {t('admin.users.stats.banned')}
            </div>
            <div className="text-2xl font-heading font-bold text-danger">
              {statistics.bannedUsers}
            </div>
          </div>
          <div className="card-glass rounded-lg p-4 border border-neutral-dark">
            <div className="text-sm text-neutral-medium font-body mb-1">
              {t('admin.users.stats.admins')}
            </div>
            <div className="text-2xl font-heading font-bold text-brand-accent">
              {statistics.adminUsers}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card-glass rounded-lg p-4 mb-6 border border-neutral-dark">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.users.filters.search')}
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              placeholder={t('admin.users.filters.searchPlaceholder')}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            />
          </div>

          {/* Ban Status Filter */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.users.filters.banStatus')}
            </label>
            <select
              value={isBannedFilter === undefined ? '' : isBannedFilter.toString()}
              onChange={(e) => {
                const value = e.target.value;
                setIsBannedFilter(value === '' ? undefined : value === 'true');
                setPage(1);
              }}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            >
              <option value="">{t('admin.users.filters.all')}</option>
              <option value="false">{t('admin.users.filters.notBanned')}</option>
              <option value="true">{t('admin.users.filters.banned')}</option>
            </select>
          </div>

          {/* Admin Status Filter */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.users.filters.adminStatus')}
            </label>
            <select
              value={isAdminFilter === undefined ? '' : isAdminFilter.toString()}
              onChange={(e) => {
                const value = e.target.value;
                setIsAdminFilter(value === '' ? undefined : value === 'true');
                setPage(1);
              }}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            >
              <option value="">{t('admin.users.filters.all')}</option>
              <option value="false">{t('admin.users.filters.regular')}</option>
              <option value="true">{t('admin.users.filters.admin')}</option>
            </select>
          </div>

          {/* ترتيب حسب */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.users.filters.sortBy')}
            </label>
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as 'balance' | 'createdAt');
                setPage(1);
              }}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            >
              <option value="createdAt">{t('admin.users.sort.joinDate')}</option>
              <option value="balance">{t('admin.users.sort.balance')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.users.filters.sortOrder')}
            </label>
            <select
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(parseInt(e.target.value) as 1 | -1);
                setPage(1);
              }}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            >
              <option value="-1">{t('admin.users.sort.descending')}</option>
              <option value="1">{t('admin.users.sort.ascending')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users List */}
      {users.length === 0 ? (
        <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
          <p className="text-neutral-medium text-lg font-body">
            {t('admin.users.empty')}
          </p>
        </div>
      ) : (
        <>
          {/* Mobile Cards View */}
          <div className="md:hidden space-y-4 mb-6">
            {users.map((user) => (
              <UserCard
                key={user._id}
                user={user}
                onBan={handleBanClick}
                onRoleChange={handleRoleClick}
                onSupportChange={handleSupportClick}
                onDelete={handleDeleteClick}
                isTogglingBan={isTogglingBan}
                isUpdatingRole={isUpdatingRole}
                isDeleting={isDeleting}
              />
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block card-glass rounded-2xl shadow-2xl overflow-hidden animate-scale-in">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-dark/50">
                  <tr>
                    <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                      {t('admin.users.table.name')}
                    </th>
                    <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                      {t('admin.users.table.contact')}
                    </th>
                    <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                      {t('admin.users.table.balance')}
                    </th>
                    <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                      {t('admin.users.table.status')}
                    </th>
                    <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                      {t('admin.users.table.created')}
                    </th>
                    <th className="px-3 sm:px-6 py-4 text-center text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                      {t('admin.users.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-dark">
                  {users.map((user) => (
                    <tr key={user._id} className="hover:bg-neutral-dark/30 transition-colors">
                      <td className="px-3 sm:px-6 py-4">
                        <div>
                          <div className="font-heading font-semibold text-neutral-light">
                            {user.firstName} {user.lastName || ''}
                          </div>
                          {user.username && (
                            <div className="text-sm text-neutral-medium font-body">
                              @{user.username}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4">
                        <div className="flex flex-col gap-1">
                          {user.email && (
                            <div className="text-xs sm:text-sm text-neutral-light font-body break-all">
                              {user.email}
                            </div>
                          )}
                          {user.telegramId && (
                            <div className="text-xs sm:text-sm text-neutral-medium font-body">
                              Telegram: {user.telegramId}
                            </div>
                          )}
                          {!user.telegramId && user.legacyTelegramId && (
                            <div className="text-xs sm:text-sm text-neutral-medium font-body flex items-center gap-1">
                              Telegram: {user.legacyTelegramId}
                              <span className="inline-block px-1.5 py-0.5 rounded-full text-[10px] font-body bg-brand-accent/20 text-brand-accent">
                                {t('admin.users.details.info.migrated')}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <div className="text-xs sm:text-sm font-heading font-semibold text-brand-accent">
                            {parseFloat(user.balance.toString()).toFixed(2)} USD
                          </div>
                          {user.referralBalance > 0 && (
                            <div className="text-xs text-neutral-medium font-body">
                              Ref: {parseFloat(user.referralBalance.toString()).toFixed(2)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4">
                        <div className="flex flex-col gap-2">
                          <span
                            className={`inline-block px-2 py-1 rounded-full text-xs font-body w-fit ${
                              user.isBanned
                                ? 'bg-danger/20 text-danger'
                                : 'bg-green-500/20 text-green-500'
                            }`}
                          >
                            {user.isBanned
                              ? t('admin.users.status.banned')
                              : t('admin.users.status.active')}
                          </span>
                          {user.isAdmin && (
                            <span className="inline-block px-2 py-1 rounded-full text-xs font-body w-fit bg-brand-accent/20 text-brand-accent">
                              {t('admin.users.status.admin')}
                            </span>
                          )}
                          {user.role === 'admin_support' && (
                            <span className="inline-block px-2 py-1 rounded-full text-xs font-body w-fit bg-blue-500/20 text-blue-500">
                              {t('admin.users.status.support')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <span className="text-xs sm:text-sm text-neutral-medium font-body">
                          {formatDate(user.originalJoinedAt || user.createdAt, 'PP h:mm a', currentLanguage, 'Asia/Damascus')}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4">
                        <div className="flex items-center justify-center gap-1 sm:gap-2 flex-wrap">
                          <Link
                            to={`/admin/users/${user._id}`}
                            className="px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
                            title={t('admin.users.actions.view')}
                          >
                            {t('admin.users.actions.view')}
                          </Link>
                          <button
                            onClick={() =>
                              handleBanClick(
                                user._id,
                                `${user.firstName} ${user.lastName || ''}`,
                                user.isBanned
                              )
                            }
                            disabled={isTogglingBan}
                            className={`px-3 py-1 rounded-lg transition-colors text-sm font-body ${
                              user.isBanned
                                ? 'bg-green-500/20 text-green-500 hover:bg-green-500/30'
                                : 'bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30'
                            }`}
                            title={
                              user.isBanned
                                ? t('admin.users.actions.unban')
                                : t('admin.users.actions.ban')
                            }
                          >
                            {user.isBanned
                              ? t('admin.users.actions.unban')
                              : t('admin.users.actions.ban')}
                          </button>
                          {user.role !== 'admin_support' && (
                            <button
                              onClick={() =>
                                handleSupportClick(
                                  user._id,
                                  `${user.firstName} ${user.lastName || ''}`,
                                  user.role === 'admin_support'
                                )
                              }
                              disabled={isUpdatingRole}
                              className="px-3 py-1 bg-blue-500/20 text-blue-500 rounded-lg hover:bg-blue-500/30 transition-colors text-sm font-body"
                              title={t('admin.users.actions.makeSupport')}
                            >
                              {t('admin.users.actions.makeSupport')}
                            </button>
                          )}
                          {user.role === 'admin_support' && (
                            <button
                              onClick={() =>
                                handleSupportClick(
                                  user._id,
                                  `${user.firstName} ${user.lastName || ''}`,
                                  user.role === 'admin_support'
                                )
                              }
                              disabled={isUpdatingRole}
                              className="px-3 py-1 bg-orange-500/20 text-orange-500 rounded-lg hover:bg-orange-500/30 transition-colors text-sm font-body"
                              title={t('admin.users.actions.removeSupport')}
                            >
                              {t('admin.users.actions.removeSupport')}
                            </button>
                          )}
                          {!user.isAdmin && (
                            <button
                              onClick={() =>
                                handleRoleClick(
                                  user._id,
                                  `${user.firstName} ${user.lastName || ''}`,
                                  user.isAdmin
                                )
                              }
                              disabled={isUpdatingRole}
                              className="px-3 py-1 bg-blue-500/20 text-blue-500 rounded-lg hover:bg-blue-500/30 transition-colors text-sm font-body"
                              title={t('admin.users.actions.makeAdmin')}
                            >
                              {t('admin.users.actions.makeAdmin')}
                            </button>
                          )}
                          {user.isAdmin && (
                            <button
                              onClick={() =>
                                handleRoleClick(
                                  user._id,
                                  `${user.firstName} ${user.lastName || ''}`,
                                  user.isAdmin
                                )
                              }
                              disabled={isUpdatingRole}
                              className="px-3 py-1 bg-orange-500/20 text-orange-500 rounded-lg hover:bg-orange-500/30 transition-colors text-sm font-body"
                              title={t('admin.users.actions.removeAdmin')}
                            >
                              {t('admin.users.actions.removeAdmin')}
                            </button>
                          )}
                          {!user.isAdmin && (
                            <button
                              onClick={() =>
                                handleDeleteClick(
                                  user._id,
                                  `${user.firstName} ${user.lastName || ''}`
                                )
                              }
                              disabled={isDeleting}
                              className="px-3 py-1 bg-danger/20 text-danger rounded-lg hover:bg-danger/30 transition-colors text-sm font-body disabled:opacity-50"
                              title={t('admin.users.actions.delete')}
                            >
                              {t('admin.users.actions.delete')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('catalog.pagination.previous')}
              </button>

              <div className="px-4 py-2 bg-neutral-dark/50 text-neutral-light font-body rounded-lg">
                {t('catalog.pagination.page')} {pagination.page} {t('catalog.pagination.of')} {pagination.pages}
              </div>

              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('catalog.pagination.next')}
              </button>
            </div>
          )}

          {/* Pagination Info */}
          {pagination && (
            <div className="text-center text-neutral-medium font-body mt-4">
              {t('catalog.pagination.showing')} {users.length} {t('catalog.pagination.of')} {pagination.total} {t('admin.users.pagination.users')}
            </div>
          )}
        </>
      )}

      {/* Ban/Unban Confirmation Dialog */}
      <ConfirmDialog
        isOpen={banDialog.isOpen}
        onClose={() => setBanDialog({ isOpen: false, userId: null, userName: '', isBanned: false })}
        onConfirm={handleBanConfirm}
        title={
          banDialog.isBanned
            ? t('admin.users.unbanTitle')
            : t('admin.users.banTitle')
        }
        message={
          banDialog.isBanned
            ? t('admin.users.unbanConfirm', { name: banDialog.userName })
            : t('admin.users.banConfirm', { name: banDialog.userName })
        }
        confirmText={
          banDialog.isBanned
            ? t('admin.users.actions.unban')
            : t('admin.users.actions.ban')
        }
        type={banDialog.isBanned ? 'info' : 'danger'}
        isLoading={isTogglingBan}
      />

      {/* Role Change Confirmation Dialog */}
      <ConfirmDialog
        isOpen={roleDialog.isOpen}
        onClose={() => setRoleDialog({ isOpen: false, userId: null, userName: '', isAdmin: false })}
        onConfirm={handleRoleConfirm}
        title={
          roleDialog.isAdmin
            ? t('admin.users.removeAdminTitle')
            : t('admin.users.makeAdminTitle')
        }
        message={
          roleDialog.isAdmin
            ? t('admin.users.removeAdminConfirm', { name: roleDialog.userName })
            : t('admin.users.makeAdminConfirm', { name: roleDialog.userName })
        }
        confirmText={
          roleDialog.isAdmin
            ? t('admin.users.actions.removeAdmin')
            : t('admin.users.actions.makeAdmin')
        }
        type="info"
        isLoading={isUpdatingRole}
      />

      {/* Support Change Confirmation Dialog */}
      <ConfirmDialog
        isOpen={supportDialog.isOpen}
        onClose={() => setSupportDialog({ isOpen: false, userId: null, userName: '', isSupport: false })}
        onConfirm={handleSupportConfirm}
        title={
          supportDialog.isSupport
            ? t('admin.users.removeSupportTitle')
            : t('admin.users.makeSupportTitle')
        }
        message={
          supportDialog.isSupport
            ? t('admin.users.removeSupportConfirm', { name: supportDialog.userName })
            : t('admin.users.makeSupportConfirm', { name: supportDialog.userName })
        }
        confirmText={
          supportDialog.isSupport
            ? t('admin.users.actions.removeSupport')
            : t('admin.users.actions.makeSupport')
        }
        type="info"
        isLoading={isUpdatingRole}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, userId: null, userName: '' })}
        onConfirm={handleDeleteConfirm}
        title={t('admin.users.deleteTitle')}
        message={t('admin.users.deleteConfirm', { name: deleteDialog.userName })}
        confirmText={t('admin.users.actions.delete')}
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}

